import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { saveEstimate, updateEstimate } from '../../../../services/estimateService';
import { updateProperty } from '../../../../services/propertyService';
import { useAnalyticsEvents } from '../../../../hooks/useAnalytics';
import { Download, MapPin, Building2, Ruler, ChevronDown, ChevronUp } from 'lucide-react';
import { StepContainer } from './StepContainer';
import { LoadingSpinner } from '../../../ui/LoadingSpinner';
import ErrorMessage from '../../../ui/ErrorMessage';
import { calculationService } from '../../../../services/calculationService';
import { estimationDataService } from '../../../../services/estimationDataService';
import { DetailedEstimationData, EstimationResult, CategoryDiscount } from '../../../../types/estimation-items';
import { CategoryDiscountControl } from '../CategoryDiscountControl';
import { TVABreakdownPanel } from '../../TVABreakdownPanel';
import { versionDetector } from '../../../../services/estimationVersionDetector';
import { legacyAdapter } from '../../../../services/legacyEstimationAdapter';
import { generateDetailedPDF } from '../../../../utils/generateEstimatePDF';

const PROPERTY_TYPE_TRANSLATIONS = {
  'house': 'Maison',
  'apartment': 'Appartement',
  'building': 'Immeuble'
} as const;

interface ResultStepProps {
  data: DetailedEstimationData;
  onBack: () => void;
  isEditMode?: boolean;
  estimateId?: string;
  draftEstimateId?: string;
}

export const ResultStep: React.FC<ResultStepProps> = ({ data, onBack, isEditMode = false, estimateId, draftEstimateId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { trackEstimationEvent, trackButtonClick } = useAnalyticsEvents();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [estimationResult, setEstimationResult] = useState<EstimationResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [appliedDiscounts, setAppliedDiscounts] = useState<CategoryDiscount[]>(data.discounts || []);
  const [adaptationWarning, setAdaptationWarning] = useState<{
    originalTotal: number;
    newTotal: number;
    difference: number;
    percentageDiff: number;
  } | null>(null);

  useEffect(() => {
    const calculateEstimate = async () => {
      try {
        setLoading(true);

        let processedData = data;
        let originalTotal: number | null = null;

        if (versionDetector.isLegacyFormat(data)) {
          console.log('🔄 Legacy estimation format detected, adapting...');

          if ((data as any).priceRange?.max) {
            originalTotal = (data as any).priceRange.max;
            console.log(`Original total from priceRange: ${originalTotal} €`);
          }

          processedData = legacyAdapter.adaptLegacyEstimation(data);
          console.log('✅ Estimation adapted to current format');

          const stats = legacyAdapter.getAdaptationStats();
          console.log('Adaptation stats:', stats);
        }

        const departmentCode = processedData.description?.location?.postcode?.substring(0, 2);
        const result = calculationService.calculateEstimate(processedData, departmentCode, appliedDiscounts);
        setEstimationResult(result);

        if (originalTotal && result.totalTTC) {
          const difference = result.totalTTC - originalTotal;
          const percentageDiff = Math.abs((difference / originalTotal) * 100);

          if (percentageDiff > 5) {
            setAdaptationWarning({
              originalTotal,
              newTotal: result.totalTTC,
              difference,
              percentageDiff
            });
            console.warn(`⚠️ Significant difference detected: ${percentageDiff.toFixed(1)}%`);
            console.warn(`Original: ${originalTotal} € -> New: ${result.totalTTC} €`);
          } else {
            console.log(`✅ Totals match within acceptable range (${percentageDiff.toFixed(1)}% difference)`);
          }
        }
      } catch (err) {
        console.error('Erreur lors du calcul:', err);
        setError('Impossible de calculer l\'estimation');
      } finally {
        setLoading(false);
      }
    };

    calculateEstimate();
  }, [data, appliedDiscounts]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (estimationResult) {
      setExpandedCategories(new Set(estimationResult.categories.map(c => c.categoryId)));
    }
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  const handleApplyDiscount = (categoryId: string, percentage: number) => {
    setAppliedDiscounts(prev => {
      const filtered = prev.filter(d => d.categoryId !== categoryId);
      return [...filtered, { categoryId, percentage }];
    });
  };

  const handleRemoveDiscount = (categoryId: string) => {
    setAppliedDiscounts(prev => prev.filter(d => d.categoryId !== categoryId));
  };

  const getDiscountForCategory = (categoryId: string): number | undefined => {
    return appliedDiscounts.find(d => d.categoryId === categoryId)?.percentage;
  };

  const handleSave = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    try {
      setSaving(true);

      let dataToSave = data;

      if (versionDetector.isLegacyFormat(data)) {
        dataToSave = legacyAdapter.adaptLegacyEstimation(data);
        dataToSave = legacyAdapter.cleanupLegacyFields({
          ...data,
          ...dataToSave,
          formatVersion: 'v2',
          migratedAt: new Date().toISOString()
        });
      }

      const propertyId = dataToSave.description?.propertyId;
      const targetId = draftEstimateId || estimateId;

      if (targetId) {
        // Draft exists (new estimate) or edit mode — update in place
        await updateEstimate(user.uid, targetId, {
          ...dataToSave,
          type: 'detailed',
          status: 'completed',
          propertyId: propertyId || null,
          discounts: appliedDiscounts,
          priceRange: {
            min: estimationResult?.totalTTC || 0,
            max: estimationResult?.totalTTC || 0,
          },
          updatedAt: new Date().toISOString(),
        });

        if (propertyId) {
          try {
            await updateProperty(user.uid, propertyId, {
              estimateId: targetId,
              lastEstimateAt: new Date().toISOString()
            });
          } catch (propertyUpdateError) {
            console.error('Erreur lors de la mise à jour de la propriété:', propertyUpdateError);
          }
        }

        trackEstimationEvent(isEditMode ? 'estimate_updated' : 'estimate_saved', 'detailed');
      } else {
        // Fallback: no draft — create from scratch
        const newEstimateId = await saveEstimate(user.uid, {
          ...dataToSave,
          type: 'detailed',
          status: 'completed',
          propertyId: propertyId || null,
          discounts: appliedDiscounts,
          priceRange: {
            min: estimationResult?.totalTTC || 0,
            max: estimationResult?.totalTTC || 0,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        if (propertyId && newEstimateId) {
          try {
            await updateProperty(user.uid, propertyId, {
              estimateId: newEstimateId,
              lastEstimateAt: new Date().toISOString()
            });
          } catch (propertyUpdateError) {
            console.error('Erreur lors de la mise à jour de la propriété:', propertyUpdateError);
          }
        }

        trackEstimationEvent('estimate_saved', 'detailed');
      }

      localStorage.removeItem('editEstimate');
      navigate('/dashboard');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde de l\'estimation');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    trackButtonClick('export_pdf', 'detailed_result');
    try {
      setGenerating(true);
      await generateDetailedPDF(data, appliedDiscounts);
    } catch (err) {
      console.error('Erreur lors de la génération du PDF:', err);
      setError('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <StepContainer title="Calcul en cours" description="" onBack={onBack} onNext={() => {}} isValid={false}>
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      </StepContainer>
    );
  }

  if (error || !estimationResult) {
    return (
      <StepContainer title="Erreur" description="" onBack={onBack} onNext={() => {}} isValid={false}>
        <ErrorMessage message={error || 'Impossible de calculer l\'estimation'} />
      </StepContainer>
    );
  }

  const cardStyle: React.CSSProperties = {
    background: 'white',
    border: '0.5px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-card)',
    padding: '18px 20px',
  };

  return (
    <StepContainer
      title="Résultat de votre estimation"
      description="Voici le détail estimatif de vos travaux"
      onBack={onBack}
      onNext={() => {}}
      isValid={true}
      hideNextButton
    >
      <div className="space-y-6">
        {adaptationWarning && (
          <div style={{ background: '#FFF7ED', borderLeft: '3px solid #FB923C', padding: '12px 14px', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0' }}>
            <p style={{ fontSize: 12, fontWeight: 500, color: '#9A3412', marginBottom: 4 }}>
              Différence détectée lors de l'adaptation
            </p>
            <p style={{ fontSize: 12, color: '#C2410C' }}>
              Le montant recalculé ({calculationService.formatPrice(adaptationWarning.newTotal)}) diffère de{' '}
              <strong>{adaptationWarning.percentageDiff.toFixed(1)}%</strong>{' '}
              du montant original ({calculationService.formatPrice(adaptationWarning.originalTotal)}).
              Cette différence peut être due à des mises à jour de prix. Le nouveau calcul utilise les prix et règles de TVA actuels.
            </p>
          </div>
        )}

        {/* Informations du bien */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 12 }}>
            Informations du bien
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <MapPin size={13} color="var(--color-text-secondary)" style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {data?.description?.location?.address || 'Non renseigné'}
                </div>
                {(data?.description?.location?.postcode || data?.description?.location?.city) && (
                  <div style={{ fontSize: 12, color: '#7C6FA0', marginTop: 2 }}>
                    {[data?.description?.location?.postcode, data?.description?.location?.city].filter(Boolean).join(' ')}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Building2 size={13} color="var(--color-text-secondary)" />
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {data?.description?.propertyType
                  ? PROPERTY_TYPE_TRANSLATIONS[data.description.propertyType as keyof typeof PROPERTY_TYPE_TRANSLATIONS]
                  : 'Non renseigné'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Ruler size={13} color="var(--color-text-secondary)" />
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {data?.description?.area ? `${data.description.area} m²` : 'Non renseigné'}
              </span>
            </div>
          </div>
        </div>

        {/* Estimation totale */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: 15, fontWeight: 500, color: 'var(--color-primary)', marginBottom: 14 }}>
            Estimation totale
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Total HT</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-primary)', fontWeight: 500 }}>
                {calculationService.formatPrice(estimationResult.totalHT)}
              </span>
            </div>
            {estimationResult.totalDiscountAmount && estimationResult.totalDiscountAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>dont décotes volume</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  − {calculationService.formatPrice(estimationResult.totalDiscountAmount)}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Total TVA</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {calculationService.formatPrice(estimationResult.tva)}
              </span>
            </div>
            <div style={{ borderTop: '0.5px solid var(--color-border)', paddingTop: 10, marginTop: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 15, fontWeight: 500, color: '#1E1148' }}>Total TTC</span>
              <span style={{ fontSize: 18, fontWeight: 500, color: '#1E1148' }}>
                {calculationService.formatPrice(estimationResult.totalTTC)}
              </span>
            </div>
          </div>
          <p style={{ fontSize: 11, color: '#7C6FA0', fontStyle: 'italic', marginTop: 14 }}>
            Les prix dans SimulReno sont basés sur le marché à début 2026. Ils sont volontairement surévalués d'environ 10%. Il est toujours préférable de provisionner une marge pour les aléas lorsque l'on se lance dans une rénovation.
          </p>
        </div>

        {/* Détail par catégorie */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>
              Détail par catégorie
            </h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={expandAll} style={{ fontSize: 12, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                Tout développer
              </button>
              <button onClick={collapseAll} style={{ fontSize: 12, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                Tout réduire
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {estimationResult.categories.map(category => (
              <div key={category.categoryId} style={{ border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                <button
                  onClick={() => toggleCategory(category.categoryId)}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F5F3FF')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {expandedCategories.has(category.categoryId)
                      ? <ChevronUp size={16} color="#7C6FA0" />
                      : <ChevronDown size={16} color="#7C6FA0" />}
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>{category.categoryTitle}</span>
                    <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>({category.items.length} éléments)</span>
                  </div>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>
                      {calculationService.formatPrice(category.subtotal)}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--color-text-secondary)', fontWeight: 400 }}>HT</span>
                  </span>
                </button>

                {expandedCategories.has(category.categoryId) && (
                  <div style={{ borderTop: '0.5px solid var(--color-border)', padding: '12px 12px', background: 'rgba(0,0,0,0.01)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {category.items.map((item, idx) => (
                        <div key={item.itemKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: idx < category.items.length - 1 ? '0.5px solid var(--color-border)' : 'none' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>{item.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                              {item.quantity} {item.unit} × {calculationService.formatPrice(item.unitPrice)}
                            </div>
                            <div style={{ fontSize: 11, color: '#7C6FA0', marginTop: 1 }}>
                              TVA {item.tvaRate}% : {calculationService.formatPrice(item.tvaAmount)}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>HT</div>
                            <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
                              {calculationService.formatPrice(item.totalPrice)}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>TTC</div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-primary)' }}>
                              {calculationService.formatPrice(item.totalTTC)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <CategoryDiscountControl
                      categoryId={category.categoryId}
                      categoryTitle={category.categoryTitle}
                      subtotalBeforeDiscount={category.subtotalBeforeDiscount || category.subtotal}
                      currentDiscount={getDiscountForCategory(category.categoryId)}
                      onApplyDiscount={handleApplyDiscount}
                      onRemoveDiscount={handleRemoveDiscount}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <TVABreakdownPanel
          tvaBreakdown={estimationResult.tvaBreakdown}
          totalHT={estimationResult.totalHT}
          totalTVA={estimationResult.tva}
          totalTTC={estimationResult.totalTTC}
          buildingAge={data.description?.buildingAge}
        />

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={handleExport}
            disabled={generating}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '8px 16px', border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'white', color: 'var(--color-text-primary)', cursor: generating ? 'not-allowed' : 'pointer', opacity: generating ? 0.6 : 1 }}
          >
            <Download size={14} />
            {generating ? 'Génération…' : 'Exporter en PDF'}
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{ flex: 1, fontSize: 13, padding: '8px 16px', border: 'none', borderRadius: 'var(--radius-md)', background: 'var(--gradient-primary)', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? (isEditMode ? 'Mise à jour...' : 'Sauvegarde...') : (isEditMode ? "Mettre à jour l'estimation" : 'Sauvegarder cette estimation')}
          </button>
        </div>
      </div>
    </StepContainer>
  );
};

export default ResultStep;
